/**
 * Ons eigen oordeel over een woning - los van wat de markt ermee doet.
 *
 * Niet te verwarren met `Woningstatus` uit historie.ts: die zegt of het huis nog
 * te koop staat, en dat is een waarneming. Dit is een besluit. Een verdwenen
 * woning kan een kansje zijn geweest, en een woning die al maanden te koop staat
 * kan afgekeurd zijn; de twee velden horen elkaar niet te overschrijven.
 *
 * Vier standen, want drie waren er te weinig. Zonder `bekeken` is elk adres dat
 * je opent en niet meteen afkeurt weer `nieuw` bij de volgende ronde, en loop je
 * dezelfde straat drie keer na. Met `bekeken` houdt "nog te doen" zijn betekenis.
 */
import { z } from 'zod';
import { aanbodSchema } from './aanbod';
import { isoDatumTijdSchema } from './common';

export const BEOORDELINGEN = ['nieuw', 'bekeken', 'kansje', 'afgekeurd', 'definitief'] as const;

export const beoordelingSchema = z.enum(BEOORDELINGEN);
export type Beoordeling = z.infer<typeof beoordelingSchema>;

/** Wat een woning is zolang er niemand naar gekeken heeft. */
export const BEOORDELING_STANDAARD: Beoordeling = 'nieuw';

/** De stand die het Kansjes-scherm toont. Eén naam, op één plek. */
export const KANSJE: Beoordeling = 'kansje';

export const BEOORDELING_LABELS: Record<Beoordeling, string> = {
  nieuw: 'Nieuw',
  bekeken: 'Bekeken',
  kansje: 'Kansje',
  afgekeurd: 'Afgekeurd',
  definitief: 'Definitief afgekeurd',
};

/**
 * Twee soorten nee (MA-22).
 *
 * `afgekeurd` is "nu niet": komt de advertentie later terug in het aanbod, dan
 * zet de import hem terug op `nieuw` en staat hij weer in beeld - wie een huis
 * afkeurde dat daarna verdween en terugkwam, wil weten dat het er weer is.
 * `definitief` is "nooit": die blijft ook na een terugkomst uit beeld.
 *
 * Beide staan standaard niet op de kaart en niet in de tabel; vraag je er met
 * `beoordelingen=` zelf om, dan wel.
 */
export const AFGEKEURD: readonly Beoordeling[] = ['afgekeurd', 'definitief'];

export function isAfgekeurd(beoordeling: Beoordeling): boolean {
  return beoordeling === 'afgekeurd' || beoordeling === 'definitief';
}

/**
 * Hoort een woning met dit oordeel in een weergave die op `beoordelingen`
 * filtert? De geheugenversie van de zeef in `packages/db/src/queries/filter.ts`:
 * zonder lijst blijven de twee soorten nee uit beeld, met een lijst telt alleen
 * wat erin staat. De web-app haalt hiermee een punt meteen na een keuring uit
 * de cache (MA-34), in plaats van te wachten tot de volgende lading het doet.
 */
export function toontBeoordeling(
  beoordelingen: readonly Beoordeling[] | undefined,
  beoordeling: Beoordeling,
): boolean {
  return beoordelingen?.length ? beoordelingen.includes(beoordeling) : !isAfgekeurd(beoordeling);
}

/**
 * De volgorde waarin de standen in een keuzelijst staan: van "nog niets mee
 * gedaan" naar "afgehandeld". Gelijk aan de declaratievolgorde van het enum, en
 * dus ook aan de sorteervolgorde die Postgres eraan geeft - `definitief` is er
 * met `ALTER TYPE ... ADD VALUE` achteraan bij gekomen, en hoort daar ook.
 */
export const BEOORDELING_VOLGORDE: readonly Beoordeling[] = BEOORDELINGEN;

/** Of deze stand het resultaat is van een besluit, of alleen van niets doen. */
export function isBeoordeeld(beoordeling: Beoordeling): boolean {
  return beoordeling !== 'nieuw';
}

/**
 * Het oordeel zoals de extensie het op Funda ziet en zet (MA-33).
 *
 * Een Funda-pagina kent van een woning alleen het objectnummer, dus hier wordt
 * op `(bron, bronId)` gezocht en niet op ons eigen id. Wat terugkomt is wat de M
 * op de pagina nodig heeft - de stand, wanneer die gezet is, en of de woning
 * nog in het aanbod staat - plus het adres om te kunnen zien dat het om de
 * goede woning gaat. Niet de hele `Listing`: de extensie heeft aan de rest niets.
 */
export const bronBeoordelingSchema = z.object({
  id: z.uuid(),
  bron: z.string().min(1),
  bronId: z.string().min(1),
  adres: z.string().min(1),
  plaats: z.string().min(1),
  beoordeling: beoordelingSchema,
  beoordeeldOp: isoDatumTijdSchema.nullable(),
  aanbod: aanbodSchema,
  /**
   * Het nummer waaronder de extensie ernaar vroeg, als dat níet `bronId` is
   * (MA-58): Funda zette de woning onder een nieuw nummer en MamiMap vond hem
   * op het adres. De extensie hangt het oordeel aan dit nummer en keurt op `bronId`.
   */
  gevraagdAls: z.string().optional(),
});
export type BronBeoordeling = z.infer<typeof bronBeoordelingSchema>;

/**
 * Een woning op Funda zonder bekend nummer, met wat de link verder zegt (MA-58):
 * `/detail/koop/amsterdam/appartement-van-woustraat-38-4/17250535/` wordt
 * `{ id: '17250535', plaats: 'amsterdam', slug: 'appartement-van-woustraat-38-4' }`.
 * Zo gaat hij als `17250535:amsterdam/appartement-van-woustraat-38-4` over de lijn.
 */
export type FundaAdresVraag = { id: string; plaats: string; slug: string };

const FUNDA_ADRES_VRAAG = /^(\d{6,}):([a-z0-9-]{1,80})\/([a-z0-9-]{1,160})$/;

export function leesFundaAdresVraag(tekst: string): FundaAdresVraag | null {
  const treffer = FUNDA_ADRES_VRAAG.exec(tekst.trim());
  if (!treffer?.[1] || !treffer[2] || !treffer[3]) return null;
  return { id: treffer[1], plaats: treffer[2], slug: treffer[3] };
}

/**
 * Een adres of plaats als vergelijkbare sleutel: kleine letters, zonder accenten,
 * alles wat geen letter of cijfer is één streepje. `Van Woustraat 38-4` en
 * Funda's `van-woustraat-38-4` worden zo hetzelfde. Dezelfde regel staat als
 * SQL in `leesBeoordelingenPerAdres` (@mamimap/db).
 */
export function adresSleutel(tekst: string): string {
  return tekst
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Het adres uit Funda's slug: zonder het woningtype vooraan
 * (`appartement-van-woustraat-38-4` -> `van-woustraat-38-4`).
 */
export function adresUitFundaSlug(slug: string): string {
  const streep = slug.indexOf('-');
  return streep < 0 ? slug : slug.slice(streep + 1);
}

/**
 * Hoeveel objectnummers één opzoekverzoek mag dragen. Een zoekpagina van Funda
 * toont er vijftien; honderd laat ruimte voor een lange lijst zonder dat de
 * URL van het verzoek uit de hand loopt.
 */
export const BRON_BEOORDELING_MAX_IDS = 100;

/**
 * Het objectnummer zoals de extensie het uit een Funda-URL leest: alleen
 * cijfers, minstens zes. Dezelfde grens als `bronIdUitUrl`, zodat wat de
 * extensie opstuurt ook is wat de import ooit als `bron_id` wegschreef.
 */
export const bronIdSchema = z.string().regex(/^\d{6,}$/, 'Dit is geen objectnummer.');
